import { useTranslation } from 'react-i18next';

import { Patient } from '@/patient-app-common/types/Patient';
import { PatientDates } from '@/patient-app-common/hooks/usePatientDates';

import { EndCard } from '../surgical/EndCard';
import { HealthCheckCard } from '../surgical/HealthCheckCard';
import { PostopCard } from '../surgical/PostopCard';

import { EndSurveyCardMat } from './EndSurveyCardMat';
import { PreopCardMat } from './PreopCardMat';
import { PeriopCardMat } from './PeriopCardMat';

interface MaternityProgramProps {
  patient: Patient;
  patientDates: PatientDates;
  programPhase: undefined | string;
}

export function MaternityProgram({
  patient,
  patientDates,
  programPhase,
}: MaternityProgramProps) {
  const { t } = useTranslation('home');
  const { confirmedSurgery, firstName, othersUsingApp } = patient;
  const headerText = othersUsingApp
    ? t('common.text.greeting_name_indirect', { firstName })
    : t('common.text.greeting_name', { firstName });
  const { surgeryDate, dischargeDate, startOfDayInHospitalTz } = patientDates;

  if (programPhase === 'inPreop' || !confirmedSurgery) {
    return (
      <PreopCardMat
        dueDate={surgeryDate}
        headerText={headerText}
        startOfDayInHospitalTz={startOfDayInHospitalTz}
      />
    );
  }

  switch (programPhase) {
    case 'inPeriop':
      return (
        <PeriopCardMat
          confirmedSurgery={confirmedSurgery}
          dueDate={surgeryDate}
          headerText={headerText}
          startOfDayInHospitalTz={startOfDayInHospitalTz}
        />
      );
    case 'inPostop':
      return (
        <>
          <PostopCard
            patient={patient}
            dischargeDate={dischargeDate}
            startOfDayInHospitalTz={startOfDayInHospitalTz}
          />
          <HealthCheckCard patient={patient} patientDates={patientDates} />
        </>
      );
    case 'end':
      return (
        <>
          <EndCard headerText={headerText} />
          <EndSurveyCardMat
            endSurveyId={patient.endSurveyId}
            endSurveyCompleted={patient.endSurveyCompleted}
          />
        </>
      );
    default:
      return <div />;
  }
}
